import React, { useState, useEffect } from 'react';
import { ArrowUp, Mail, Code2 } from 'lucide-react';
import { GithubIcon, LinkedinIcon } from '../ui/Icons';
import { useLanguage } from '../../contexts/LanguageContext';
import '../../styles/components/Footer.css';

const Footer: React.FC = () => {
  const { t } = useLanguage();
  const [showBackToTop, setShowBackToTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > window.innerHeight);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const navLinks = [
    { id: 'hero', label: t('nav.home') },
    { id: 'about', label: t('nav.about') },
    { id: 'team', label: t('nav.team') },
    { id: 'skills', label: t('nav.skills') },
    { id: 'projects', label: t('nav.projects') },
    { id: 'contact', label: t('nav.contact') },
  ];

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      const offset = 80;
      const top = el.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  return (
    <footer className="footer">
      <div className="container">
        <div className="footer__grid">
          <div className="footer__brand">
            <span className="footer__logo">Famini<span className="footer__logo-plus">+</span></span>
            <p className="footer__description">{t('footer.description')}</p>
          </div>

          <div className="footer__nav">
            <h4 className="footer__heading">{t('footer.quickLinks')}</h4>
            <ul className="footer__list">
              {navLinks.map(link => (
                <li key={link.id}>
                  <button className="footer__link" onClick={() => scrollToSection(link.id)}>
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="footer__connect">
            <h4 className="footer__heading">{t('footer.connect')}</h4>
            <div className="footer__social">
              <a href="https://github.com" target="_blank" rel="noopener noreferrer" className="footer__social-link" aria-label="GitHub">
                <GithubIcon size={20} />
              </a>
              <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="footer__social-link" aria-label="LinkedIn">
                <LinkedinIcon size={20} />
              </a>
              <a href="mailto:dangminhthanh.dut@gmail.com" className="footer__social-link" aria-label="Email">
                <Mail size={20} />
              </a>
            </div>
          </div>
        </div>

        <div className="footer__bottom">
          <p className="footer__copyright">{t('footer.copyright')}</p>
          <div className="footer__badge">
            <Code2 size={14} />
            <span>{t('footer.builtWith')}</span>
          </div>
        </div>
      </div>

      <button
        className={`back-to-top ${showBackToTop ? 'back-to-top--visible' : ''}`}
        onClick={scrollToTop}
        aria-label="Back to top"
      >
        <ArrowUp size={20} />
      </button>
    </footer>
  );
};

export default Footer;
